<template>
  <div>
    <StepTitle :value="title" class="mb-8" />

    <div v-if="user.signedIn">
      <ul class="m-0 list-none p-0">
        <li class="flex items-center justify-between py-4">
          <span class="text-lg font-medium">La messagerie privée de Codeur.com</span>
          <CToggleInput v-model="codeurInbox" disabled name="codeur-inbox" />
        </li>
        <li class="flex items-center justify-between py-4">
          <span>
            <span class="text-lg font-medium">{{ content.emailLabel }}</span>
            <span class="block text-sm text-gray-500 md:inline">{{ content.emailHint }}</span>
          </span>
          <CToggleInput v-model="email.display" name="show-email" />
        </li>
        <li class="flex items-center justify-between py-4">
          <span>
            <span class="text-lg font-medium">{{ content.phoneLabel }}</span>
            <span class="block text-sm text-gray-500 md:inline">{{ content.phoneHint }}</span>
          </span>
          <CToggleInput v-model="phone.display" name="show-phone" />
        </li>
        <div>
          <div
            v-if="phone.display && (!user.phone || user.phone.length === 0)"
            class="form-group phone-form-group relative mb-0 mt-2"
            :class="{ 'form-group-invalid': hasPhoneError }"
          >
            <PhoneInput
              :value="phone.value"
              :default-country="defaultCountry"
              :errors="mainStore.errors.user_user_contact_phone"
              :required="phone.display"
              @input="
                phone.value = $event.value
                phone.cleaned = $event.cleaned
              "
            />
          </div>
        </div>
      </ul>
    </div>

    <div v-else>
      <fieldset class="mb-0">
        <div class="form-group" :class="{ 'form-group-invalid': hasEmailError }">
          <label for="fast-project-email" class="px-0.5 text-body-color">{{ content.emailLabel }}</label>
          <input
            id="fast-project-email"
            ref="input"
            v-model="email.value"
            name="project[user_attributes][email]"
            type="email"
            class="form-control h-auto rounded-lg py-4"
            :class="{ 'is-invalid': hasEmailError }"
            :placeholder="mainStore.content.steps.contact.placeholder || 'Votre adresse email'"
            autocomplete="email"
            tabindex="-1"
          />
          <div v-if="hasEmailError" class="invalid-feedback">
            Votre adresse email {{ errors['user.email'] | toSentence }}.
          </div>
          <div class="mt-2 flex items-center justify-end space-x-3">
            <label for="show-email-input" class="mb-0 text-body-color">Visible sur l'annonce</label>
            <CToggleInput v-model="email.display" name="show-email" />
          </div>
        </div>
      </fieldset>

      <fieldset class="flex flex-col gap-3">
        <button
          id="toggle-advanced"
          type="button"
          class="flex gap-3 items-center bg-white p-0"
          :aria-expanded="advancedOpen.toString()"
          aria-controls="advanced-panel-guest"
          @click="toggleAdvanced"
        >
          <span class="text-normal font-medium">Paramètres avancés</span>
          <span class="inline-flex shrink-0 transition-transform" :class="{ 'rotate-180': advancedOpen }" aria-hidden="true">
            <fa-icon type="far" icon="chevron-down" />
          </span>
        </button>
        <div v-show="advancedOpen" id="advanced-panel-guest">
          <div class="form-group phone-form-group relative mb-0" :class="{ 'form-group-invalid': hasPhoneError }">
            <label for="fast-project-phone" class="mx-0.5 text-body-color">{{ content.phoneLabel }}</label>
            <PhoneInput
              :value="phone.value"
              :default-country="defaultCountry"
              :errors="mainStore.errors.user_user_contact_phone"
              :required="phone.display"
              @input="
                phone.value = $event.value
                phone.cleaned = $event.cleaned
              "
            />
          </div>
          <div class="mt-2 flex items-center justify-end space-x-3">
            <label for="show-phone-input" class="mb-0 text-body-color">Visible sur l'annonce</label>
            <CToggleInput v-model="phone.display" name="show-phone" />
          </div>
        </div>
      </fieldset>
    </div>

    <div v-if="!mainStore.user.signedIn" class="mt-12" v-html="mainStore.currentStepContent.reassurance"></div>

    <portal v-if="active" to="fast-footer">
      <StepNav
        :next-disabled="!completed || mainStore.submitting"
        @goToPrev="mainStore.goToPreviousStep()"
        @goToNext="proceed"
      />
    </portal>

    <PasswordModal :visible="requestPassword" :email="email.value" @close="closePasswordModal" />
  </div>
</template>

<script>
import { mapStores } from 'pinia'
import { ALERTS, STEPS, useMainStore } from '@/apps/fast/store'

import PhoneInput from './PhoneInput.vue'
import PasswordModal from './PasswordModal.vue'
import CToggleInput from '@/apps/shared/components/ToggleInput.vue'

export default {
  components: {
    PhoneInput,
    PasswordModal,
    CToggleInput
  },
  data() {
    return {
      proceeding: false,
      codeurInbox: true,
      email: {
        display: false,
        value: ''
      },
      loginToken: null,
      requestPassword: false,
      phone: {
        display: false,
        value: '',
        cleaned: ''
      },
      advancedOpen: false
    }
  },
  computed: {
    ...mapStores(useMainStore),
    active() {
      return this.mainStore.step === STEPS.contact
    },
    content() {
      return this.mainStore.stepContent(STEPS.contact)
    },
    title() {
      if (this.user.signedIn) {
        return this.content?.title
      } else {
        return this.content.guestTitle
      }
    },
    user: {
      get() {
        return this.mainStore.user
      },
      set(user) {
        this.mainStore.user = user
      }
    },
    defaultCountry() {
      return this.user.country || 'FR'
    },
    completed() {
      return (this.user.signedIn || this.validEmail) && (!this.phone.display || this.validPhone)
    },
    disabled() {
      return !this.completed || this.proceeding
    },
    validEmail() {
      return !!this.email.value.match(/^[\w+-]+(\.[\w+-]+)*@[a-z\d-]+(\.[a-z\d-]+)*\.[a-z]{2,}$/i)
    },
    validPhone() {
      return this.phone.cleaned !== null
    },
    hasEmailError() {
      return this.mainStore.errors['user.email'] && this.mainStore.errors['user.email'].length > 0
    },
    hasPhoneError() {
      // We clear phone if invalid, no friction for user if he inputs bullshit
      return false
    },
    withPhone() {
      return this.phone.display && this.validPhone
    },
    contactMode() {
      if (this.email.display && this.withPhone) {
        return 'all'
      } else if (!this.email.display && this.withPhone) {
        return 'phone'
      } else if (this.email.display && !this.withPhone) {
        return 'mail'
      } else {
        return 'no'
      }
    }
  },
  methods: {
    async enter() {
      if (!this.phone.value) this.phone.value = this.user.phone ?? ''
      this.$root.sendMatomoPageView('contact')
      this.proceeding = false
      document.addEventListener('keyup', this.proceedByKeyboard)
      const user = await this.$root.checkUser()
      this.mainStore.user.email = user.email
      if (!this.$root.isAndroid()) this.$refs.input?.focus()
    },
    exit() {
      document.removeEventListener('keyup', this.proceedByKeyboard)
    },
    proceedByKeyboard(event) {
      if (event.key === 'Enter' && this.completed && !this.requestPassword) {
        this.proceed()
      }
    },
    async proceed() {
      if (!this.completed) return

      this.proceeding = true
      this.mainStore.requestBody.project.contact_info = this.contactMode

      if (this.user.signedIn) {
        if (!this.mainStore.user.completeContactInfo) {
          this.goToLocationStep()
        } else {
          this.submitProjectRecordAndGoToNextStep()
        }
      } else {
        this.mainStore.user.email = this.email.value
        this.mainStore.requestBody.project.user_attributes.email = this.email.value
        this.mainStore.requestBody.project.user_user_contact_phone = this.phone.cleaned
        const user = await this.$root.checkUser(this.email.value)
        if (user.unpublished) {
          this.mainStore.alert = ALERTS.awaiting
        } else if (user.spamming) {
          this.mainStore.alert = ALERTS.spamming
        } else if (user.unactivated) {
          this.requestPassword = true
        } else {
          if (user.typology === 'guest' || !user.contact) {
            this.goToLocationStep()
          } else {
            // Existing user can't have "options" step in order to avoid
            // login during user flow.
            this.submitProjectRecordAndGoToNextStep(true)
          }
        }
      }
    },
    async submitProjectRecordAndGoToNextStep(skipOptions = false) {
      try {
        await this.$root.createAndStoreProject()
        if (skipOptions) {
          this.$root.sendMatomoPageView('force-skip-options')
          this.mainStore.alert = ALERTS.success
        } else {
          this.mainStore.goToNextStep()
        }
      } catch (error) {
        this.goToFirstErroredStep()
      }
    },
    goToFirstErroredStep() {
      const found = this.mainStore.steps.find(
        step => Array.isArray(this.mainStore.errors[step]) && this.mainStore.errors[step].length > 0
      )
      if (typeof found === 'string') {
        this.mainStore.goToStep(found)
      }
    },
    goToLocationStep() {
      this.mainStore.needLocation = true
      this.mainStore.goToNextStep()
    },
    closePasswordModal(loginToken = null) {
      this.requestPassword = false

      if (loginToken) {
        this.loginToken = loginToken
        this.mainStore.requestBody.login_token = this.loginToken
      }
      if (this.loginToken) this.$nextTick(() => this.submitProjectRecordAndGoToNextStep())
    },
    toggleAdvanced() {
      this.advancedOpen = !this.advancedOpen
    }
  }
}
</script>
